// Карточка договора (смарт-процесс 153, «Реестр договоров»): транспорт того, чего нет
// в api/hooks.ts.
//
// Сама запись читается существующими хуками (useItem → crm.item.get, useFields →
// /fields?entity=item:153) — здесь только два дополнения:
//
//   1) СЛЕД СВОЕЙ СХЕМЫ. Договор перенесён в свою таблицу `contract`, и её номер строки
//      (own_id) — ЕДИНСТВЕННЫЙ ключ, которым движок процессов помнит договор
//      (bpe_tasks.entity='договор', doc_id=contract.id — проверено запросом к боевой базе
//      31.07.2026). Номер Bitrix движку неизвестен, поэтому без этого запроса задания
//      движка не найти. Ручка уже есть: GET /api/docs/item?src=own&type=contracts&id=…
//      (docs_api.карточка_own; id принимает и номер Bitrix, и «own-N»).
//
//   2) ЗАДАНИЯ ДВИЖКА. GET /api/bpe/tasks?entity=договор&doc_id=<own id>&state=all —
//      та же ручка, что у раздела «Задания движка» (bpe_api.список_заданий, фильтры
//      entity/doc_id есть в ЗАД_ФИЛЬТРЫ; проверено вызовом на 127.0.0.1:8080).
//
// Плюс фильтр «сделки этого договора»: связь живёт В СДЕЛКЕ, полем-привязкой
// UF_CRM_1621413940 «Реестр договоров» (crm, multiple). В зеркале это jsonb-массив
// номеров записей 153: `["5708"]`. Ядро фильтрует UF-поля по ТЕКСТУ raw->>'поле'
// (build_where в core_api.py), поэтому подстрока `"5708"` — В КАВЫЧКАХ — находит ровно
// договор 5708: значения в массиве всегда закавычены, и «5708» не совпадёт с куском
// «15708». Проверено вызовом /bx 31.07.2026: фильтр {%UF_CRM_1621413940: "\"5708\""}
// отдал те же 2 сделки, что raw->'…' @> '"5708"' в SQL.
//
// ЗАПИСИ ЗДЕСЬ НЕТ И НЕ БУДЕТ БЕЗ РЕШЕНИЯ ВЛАДЕЛЬЦА: реестр наполняют 10 бизнес-процессов
// Bitrix, и запись из CRM их перезапустила бы (см. SMART_TYPES в lib/smart.ts).

import { useQuery } from '@tanstack/react-query'

import { Unauthorized } from './client'

/** Поле-привязка «Реестр договоров» в СДЕЛКЕ (crm, multiple) — связь сделка → договор. */
export const ПОЛЕ_ДОГОВОРА_В_СДЕЛКЕ = 'UF_CRM_1621413940'

/**
 * Фильтр «сделки, привязанные к договору №id». Кавычки вокруг номера обязательны —
 * см. шапку файла: без них номер совпал бы с куском чужого номера.
 */
export const фильтрСделокДоговора = (id: string): Record<string, unknown> =>
  ({ [`%${ПОЛЕ_ДОГОВОРА_В_СДЕЛКЕ}`]: `"${id}"` })

// ─────────────────────────── след своей схемы ───────────────────────────

/** Строка `contract` своей схемы, как её отдаёт docs_api.карточка_own (поля — по пробе). */
export interface СвойДоговор {
  own_id: number
  bitrix_id: number | null
  doc_state: string | null
  /** Подпись состояния словами ядра: Черновик / Выпущен / Аннулирован. */
  state_name: string | null
  doc_number: string | null
  doc_date: string | null
  due_date: string | null
  company_bitrix_id: number | null
  company_title: string | null
  own_company_title: string | null
  deal_bitrix_id: number | null
  assigned_bitrix_id: number | null
  assigned_name: string | null
}

/** Добавка карточки own: заполненная колонка вида (kind_id уже именем, не числом). */
export interface ДобавкаOwn { code: string; value: unknown }

/**
 * Ответ «следа»: три исхода — свой номер есть; его честно нет (с причиной ядра или BFF);
 * сеть/ядро упали (это error запроса, не данные).
 */
export type СледДоговора =
  | { вид: 'есть'; doc: СвойДоговор; поля: ДобавкаOwn[] }
  | { вид: 'нет'; почему: string }

const БЕЗ_ДОСТУПА =
  'раздел «Документы по сделке» вашей роли не открыт — след своей схемы не прочитать'

/**
 * След договора в своей схеме. 404 и 403 — ОТВЕТЫ, а не ошибки: «не перенесён» и
 * «не положено» карточка обязана сказать словами, а не красной плашкой; поэтому оба
 * приходят данными (`вид: 'нет'`), и только настоящий обрыв становится error.
 */
export const useOwnContract = (id: string | undefined) =>
  useQuery({
    queryKey: ['contract-own', id],
    enabled: !!id,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<СледДоговора> => {
      const res = await fetch(
        `/api/docs/item?src=own&type=contracts&id=${encodeURIComponent(id!)}`,
        { credentials: 'same-origin' },
      )
      if (res.status === 401) throw new Unauthorized()
      const текст = await res.text()
      const данные = (текст ? JSON.parse(текст) : {}) as Record<string, unknown>
      if (res.status === 403) return { вид: 'нет', почему: БЕЗ_ДОСТУПА }
      if (res.status === 404) {
        return {
          вид: 'нет',
          почему: String(данные.why ?? 'договора в своей схеме нет (перенос его не взял)'),
        }
      }
      if (!res.ok) throw new Error(String(данные.error ?? данные.detail ?? `HTTP ${res.status}`))
      return {
        вид: 'есть',
        doc: данные.doc as СвойДоговор,
        поля: (Array.isArray(данные.fields) ? данные.fields : []) as ДобавкаOwn[],
      }
    },
  })

// ─────────────────────────── задания движка ───────────────────────────

/** Задание движка — поля ответа /bpe/tasks, которые карточка показывает (по пробе). */
export interface ЗаданиеДвижка {
  id: number
  kind: string
  name: string
  body?: string | null
  entity: string
  doc_id: string
  due_at?: string | null
  due_in_days?: number | null
  due_state?: string | null
  created_at?: string | null
  closed_at?: string | null
  outcome?: string | null
  process?: string | null
  instance_caption?: string | null
  assignees_info?: Array<{ id?: unknown; name?: string | null; active?: boolean }>
}

export type ЗаданияДоговора =
  | { вид: 'есть'; rows: ЗаданиеДвижка[]; total: number; engine_started: boolean }
  | { вид: 'нет'; почему: string }

/**
 * Задания движка по договору. Ключ — СВОЙ номер (contract.id), поэтому запрос включается
 * только когда след своей схемы найден. `state=all`: карточке нужна и история — закрытое
 * задание «нужна ли пролонгация» объясняет, почему договор продлён.
 */
export const useContractTasks = (ownId: number | null | undefined) =>
  useQuery({
    queryKey: ['contract-bpe-tasks', ownId],
    enabled: ownId !== null && ownId !== undefined,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<ЗаданияДоговора> => {
      const п = new URLSearchParams({
        entity: 'договор', doc_id: String(ownId), state: 'all', sort: 'new',
      })
      const res = await fetch(`/api/bpe/tasks?${п.toString()}`, { credentials: 'same-origin' })
      if (res.status === 401) throw new Unauthorized()
      const текст = await res.text()
      const данные = (текст ? JSON.parse(текст) : {}) as Record<string, unknown>
      if (res.status === 403) {
        return { вид: 'нет', почему: 'раздел «Задания движка» вашей роли не открыт' }
      }
      if (!res.ok) throw new Error(String(данные.why ?? данные.error ?? `HTTP ${res.status}`))
      return {
        вид: 'есть',
        rows: (Array.isArray(данные.rows) ? данные.rows : []) as ЗаданиеДвижка[],
        total: Number(данные.total ?? 0),
        engine_started: данные.engine_started !== false,
      }
    },
  })
